import Parse from 'parse';
import { WarnAlert, ErrorAlert } from 'helpers/alert'; // Assuming ErrorAlert is also available
import history from 'core/history';
import store from '../store/configureStore'; // Direct store import
import { logoutUser } from '../actions/user';

// Note: store is imported directly from configureStore, which returns the store instance.
// Depending on how store is exported from '../store/configureStore.js' (default or named),
// this might need adjustment, e.g., if '../store/index.js' is the typical entry point for the configured store.
// For this exercise, we'll assume `import store from '../store/configureStore';` correctly gets the store instance.
// If `configureStore` is the function, it should be `import configureAppStore from '../store/configureStore'; const store = configureAppStore();`
// However, typically a project will have a central store export like:
// import store from '../store'; // (from an index.js in the store folder)

const ERROR_MESSAGES = {
  [Parse.Error.INVALID_SESSION_TOKEN]: 'Your session has expired. Please login again.',
  [Parse.Error.OBJECT_NOT_FOUND]: 'The requested data could not be found. It may have been deleted or you might not have permission to view it.',
  [Parse.Error.CONNECTION_FAILED]: 'Connection failed. Please check your internet connection and try again.',
  [Parse.Error.TIMEOUT]: 'The request timed out. Please try again.',
  [Parse.Error.INVALID_QUERY]: 'There was a problem with the request. Please try again.',
  [Parse.Error.INTERNAL_SERVER_ERROR]: 'A server error occurred. Please try again later.',
  [Parse.Error.OPERATION_FORBIDDEN]: 'You do not have permission to perform this action or access this resource.',
  // Example additional error codes (can be uncommented and used if needed)
  // [Parse.Error.EMAIL_TAKEN]: 'This email address is already registered.',
  // [Parse.Error.USERNAME_TAKEN]: 'This username is already taken.',
  // [Parse.Error.INVALID_EMAIL_ADDRESS]: 'Invalid email address format.',
  [Parse.Error.OTHER_CAUSE]: 'An unknown error occurred. Please try again.', // Default for other codes
};

export default function handleParseError(cb) {
  const callback = cb || (() => {});

  return async error => { // Made async to support await for logoutUser
    console.error('Parse Error:', error); // Log the full error object

    let redirected = false; // Flag to prevent multiple redirects or callbacks

    switch (error.code) {
      case Parse.Error.INVALID_SESSION_TOKEN:
        ErrorAlert(ERROR_MESSAGES[error.code]);
        // Check if store and dispatch are available
        if (store && typeof store.dispatch === 'function') {
          try {
            await store.dispatch(logoutUser()); // Dispatch the full logout action
            // logoutUser thunk should handle clearing state and Parse.User.logOut()
          } catch (logoutErr) {
            console.error('Error during logoutUser dispatch in handleParseError:', logoutErr);
          }
        } else {
          console.error('Store or dispatch is not available in handleParseError for logoutUser.');
          // Fallback or direct Parse logout if store dispatch fails or is unavailable
          try {
            await Parse.User.logOut();
          } catch (parseLogoutErr) {
            console.error('Error during Parse.User.logOut in handleParseError fallback:', parseLogoutErr);
          }
        }
        history.push('/login?sessionExpired=true');
        redirected = true;
        break;

      case Parse.Error.CONNECTION_FAILED:
      case Parse.Error.TIMEOUT:
        // Use a more generic message as per subtask suggestion for these network/timeout issues
        ErrorAlert('The request timed out or a network error occurred. Please check your internet connection and try again.');
        callback(error);
        break;

      case Parse.Error.OBJECT_NOT_FOUND:
      case Parse.Error.OPERATION_FORBIDDEN:
        ErrorAlert(ERROR_MESSAGES[error.code] || error.message);
        callback(error);
        break;

      default:
        {
          let message = ERROR_MESSAGES[error.code] || error.message || ERROR_MESSAGES[Parse.Error.OTHER_CAUSE];
          // Check for generic timeout messages in the default case
          if (error.message && (error.message.toLowerCase().includes('timeout') || error.message.toLowerCase().includes('timed out'))) {
            message = 'The request timed out. Please check your internet connection and try again.';
          }
          ErrorAlert(message);
          callback(error);
        }
        break;
    }

    if (typeof window !== 'undefined' && window.Sentry && !redirected) {
      // Only capture in Sentry if it wasn't an error that caused a redirect (like session expiry)
      // or if it's an error that we want to track despite user being informed.
      // For session expiry, the logout itself is the primary action.
      // You might still want to log session expiry to Sentry if it's frequent.
      window.Sentry.captureException(error);
    }
  };
}

export function handleParseErrorAsync(error) {
  return new Promise((resolve, reject) => {
    let rejectedByCallback = false;
    const errorHandler = handleParseError(err => {
      // This callback is invoked for errors that are not INVALID_SESSION_TOKEN
      // and are intended to be passed back to the caller.
      rejectedByCallback = true;
      reject(err);
    });

    errorHandler(error);

    // If the error code is INVALID_SESSION_TOKEN, logout is dispatched,
    // and the page might navigate. We should reject the promise here as well
    // because the original operation that led to this error has effectively failed.
    if (error.code === Parse.Error.INVALID_SESSION_TOKEN && !rejectedByCallback) {
      reject(error);
    }
    // If an error was handled by handleParseError (e.g., an alert shown) but
    // it didn't trigger the callback (meaning it wasn't one of the cases that
    // explicitly calls callback(err)) AND it wasn't an INVALID_SESSION_TOKEN,
    // this promise would currently hang.
    // However, the refactored handleParseError calls callback() for all cases
    // except INVALID_SESSION_TOKEN, so this should be covered.
    // If there were a case where an error is only shown via Alert and not passed to callback,
    // and not INVALID_SESSION_TOKEN, one might consider resolving here, but typically,
    // an async error handler is expected to reject if any error occurred and was handled.
  });
}
